"use client";

import { motion } from "framer-motion";
import { Calculator, TrendingUp, Clock, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/SectionHeading";

const useCases = [
  {
    title: "E-commerce Order Processing",
    before: {
      time: "8 hours/day",
      cost: "$200/day",
      errors: "5-10 mistakes/week",
      capacity: "100 orders/day"
    },
    after: {
      time: "15 minutes/day",
      cost: "$20/day",
      errors: "0 mistakes",
      capacity: "1,000 orders/day"
    },
    savings: {
      annual: "$65,700",
      timeBack: "7.75 hours/day",
      roi: "8.2x"
    }
  },
  {
    title: "Customer Service Automation",
    before: {
      time: "6 hours/day",
      cost: "$150/day",
      errors: "Inconsistent responses",
      capacity: "50 tickets/day"
    },
    after: {
      time: "30 minutes/day",
      cost: "$15/day",
      errors: "Perfect consistency",
      capacity: "500 tickets/day"
    },
    savings: {
      annual: "$49,275",
      timeBack: "5.5 hours/day",
      roi: "6.5x"
    }
  },
  {
    title: "Data Entry & Reporting",
    before: {
      time: "4 hours/day",
      cost: "$100/day",
      errors: "2-5 errors/week",
      capacity: "200 records/day"
    },
    after: {
      time: "10 minutes/day",
      cost: "$10/day",
      errors: "100% accuracy",
      capacity: "2,000 records/day"
    },
    savings: {
      annual: "$32,850",
      timeBack: "3.83 hours/day",
      roi: "5.5x"
    }
  }
];

const ROICalculator = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-br from-background via-primary/5 to-background">
      <div className="container max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <SectionHeading title="Calculate Your Potential Savings" className="text-center" />
          <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
            See exactly how much time and money AI automation could save your business
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {useCases.map((useCase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              className="bg-card border border-border rounded-2xl p-6 shadow-lg"
            >
              <h3 className="text-xl font-bold mb-6 text-center">{useCase.title}</h3>
              
              {/* Before/After Comparison */}
              <div className="space-y-6">
                {/* Before */}
                <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4">
                  <h4 className="font-semibold text-destructive mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Before Automation
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Time spent:</span>
                      <span className="font-medium">{useCase.before.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Daily cost:</span>
                      <span className="font-medium">{useCase.before.cost}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Errors:</span>
                      <span className="font-medium">{useCase.before.errors}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Capacity:</span>
                      <span className="font-medium">{useCase.before.capacity}</span>
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="text-center">
                  <ArrowRight className="w-6 h-6 mx-auto text-primary" />
                </div>

                {/* After */}
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <h4 className="font-semibold text-primary mb-3 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    After Automation
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Time spent:</span>
                      <span className="font-medium">{useCase.after.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Daily cost:</span>
                      <span className="font-medium">{useCase.after.cost}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Errors:</span>
                      <span className="font-medium">{useCase.after.errors}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Capacity:</span>
                      <span className="font-medium">{useCase.after.capacity}</span>
                    </div>
                  </div>
                </div>

                {/* Savings Summary */}
                <div className="bg-accent/5 border border-accent/20 rounded-xl p-4">
                  <h4 className="font-semibold text-accent mb-3 flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Your Savings
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Annual savings:</span>
                      <span className="font-bold text-accent">{useCase.savings.annual}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Time back:</span>
                      <span className="font-medium">{useCase.savings.timeBack}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>ROI:</span>
                      <span className="font-bold text-accent">{useCase.savings.roi}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center"
        >
          <Calculator className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Average Client Results
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <div className="text-3xl font-bold text-accent">$147K</div>
              <div className="text-sm text-muted-foreground">Average Annual Savings</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent">6.5x</div>
              <div className="text-sm text-muted-foreground">Average ROI</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent">80%</div>
              <div className="text-sm text-muted-foreground">Time Reduction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent">6 weeks</div>
              <div className="text-sm text-muted-foreground">Implementation Time</div>
            </div>
          </div>
          <p className="text-lg text-muted-foreground mb-6">
            These aren&apos;t projections—they&apos;re real results from businesses just like yours
          </p>
          <Button 
            size="lg" 
            className="group text-base px-8 rounded-full"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get Your Custom ROI Analysis
            <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator; 